<template>
  <div
    class="sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-x-[25px] lg:gap-[25px]"
  >
    <RevenueGrowthChart :current-user-id="currentUserId" :user-permissions="userPermissions" />

    <LeadConversionChart :current-user-id="currentUserId" :user-permissions="userPermissions" />

    <TotalOrdersChart :current-user-id="currentUserId" :user-permissions="userPermissions" />

    <AnnualProfitChart :current-user-id="currentUserId" :user-permissions="userPermissions" />
  </div>

  <div
    class="trezo-card bg-white dark:bg-[#0c1427] mb-[25px] p-[20px] md:p-[25px] rounded-md"
  >
    <div
      class="trezo-card-header mb-[20px] md:mb-[25px] sm:flex items-center justify-between"
    >
      <div class="trezo-card-title">
        <form class="relative sm:w-[265px]">
          <label
            class="leading-none absolute ltr:left-[13px] rtl:right-[13px] text-black dark:text-white mt-px top-1/2 -translate-y-1/2"
          >
            <i class="material-symbols-outlined !text-[20px]"> search </i>
          </label>
          <input
            type="text"
            :placeholder="directT('crm.leads.searchLead')"
            v-model="searchQuery"
            @input="handleSearch"
            class="bg-gray-50 border border-gray-50 h-[36px] text-xs rounded-md w-full block text-black pt-[11px] pb-[12px] ltr:pl-[38px] rtl:pr-[38px] ltr:pr-[13px] ltr:md:pr-[16px] rtl:pl-[13px] rtl:md:pl-[16px] placeholder:text-gray-500 outline-0 dark:bg-[#15203c] dark:text-white dark:border-[#15203c] dark:placeholder:text-gray-400"
          />
        </form>
      </div>

      <div class="trezo-card-subtitle mt-[15px] sm:mt-0">
        <button
          type="button"
          class="inline-block transition-all rounded-md font-medium px-[13px] py-[6px] text-primary-500 border border-primary-500 hover:bg-primary-500 hover:text-white"
          @click="stateStoreInstance.activeBtn"
        >
          <span class="inline-block relative ltr:pl-[22px] rtl:pr-[22px]">
            <i
              class="material-symbols-outlined !text-[22px] absolute ltr:-left-[4px] rtl:-right-[4px] top-1/2 -translate-y-1/2"
            >
              add
            </i>
            {{ directT('crm.leads.addNewLead') }}
          </span>
        </button>
        
        <button
          v-if="selectedLeads.length > 0"
          type="button"
          class="inline-block transition-all rounded-md font-medium px-[13px] py-[6px] text-primary-500 border border-primary-500 hover:bg-primary-500 hover:text-white ml-2"
          @click="showBulkAssignModal"
        >
          <span class="inline-block relative ltr:pl-[22px] rtl:pr-[22px]">
            <i
              class="material-symbols-outlined !text-[22px] absolute ltr:-left-[4px] rtl:-right-[4px] top-1/2 -translate-y-1/2"
            >
              person_add
            </i>
            {{ 'Assign To' }}
          </span>
        </button>
      </div>
    </div>

    <div class="trezo-card-content -mx-[20px] md:-mx-[25px]">
      <div v-if="loading" class="flex justify-center items-center p-8">
        <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
      
      <div v-else-if="leads.length === 0" class="flex justify-center items-center p-8">
        <p class="text-gray-500 dark:text-gray-400">No leads found</p>
      </div>
      
      <div v-else class="table-responsive overflow-x-auto">
        <table class="w-full">
          <thead class="text-black dark:text-white">
            <tr>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 bg-primary-50 dark:bg-[#15203c] whitespace-nowrap"
              >
                <div class="form-check relative top-[2px]">
                  <input type="checkbox" class="cursor-pointer" v-model="selectAll" @change="toggleSelectAll" />
                </div>
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 bg-primary-50 dark:bg-[#15203c] whitespace-nowrap"
              >
                {{ directT('dashboard.id') }}
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 bg-primary-50 dark:bg-[#15203c] whitespace-nowrap"
              >
                {{ directT('dashboard.customer') }}
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 bg-primary-50 dark:bg-[#15203c] whitespace-nowrap"
              >
                {{ directT('common.email') }}
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 bg-primary-50 dark:bg-[#15203c] whitespace-nowrap"
              >
                {{ directT('common.phone') }}
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 bg-primary-50 dark:bg-[#15203c] whitespace-nowrap"
              >
                {{ directT('dashboard.crm.source') }}
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 bg-primary-50 dark:bg-[#15203c] whitespace-nowrap"
              >
                {{ directT('dashboard.status') }}
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 bg-primary-50 dark:bg-[#15203c] whitespace-nowrap"
              >
                {{ directT('todoList.assignedTo') }}
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 bg-primary-50 dark:bg-[#15203c] whitespace-nowrap"
              >
                {{ directT('dashboard.crm.action') }}
              </th>
            </tr>
          </thead>
          <tbody class="text-black dark:text-white">
            <tr v-for="lead in paginatedLeads" :key="lead.id">
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[17px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 border-b border-gray-100 dark:border-[#172036]"
              >
                <div class="form-check relative top-[2px]">
                  <input type="checkbox" class="cursor-pointer" v-model="selectedLeads" :value="lead.id" />
                </div>
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[17px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 border-b border-gray-100 dark:border-[#172036]"
              >
                <span class="text-gray-500 dark:text-gray-400">{{ getShortId(lead.id) }}</span>
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[17px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 border-b border-gray-100 dark:border-[#172036]"
              >
                <div class="flex items-center">
                  <div class="rounded-md w-[40px] rounded-full">
                    <img
                      :src="getLeadImageUrl(lead)"
                      class="inline-block rounded-full"
                      alt="lead-image"
                    />
                  </div>
                  <div class="ltr:ml-[12px] rtl:mr-[12px]">
                    <span class="block font-medium">{{ lead.name }}</span>
                  </div>
                </div>
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[17px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 border-b border-gray-100 dark:border-[#172036]"
              >
                {{ lead.email }}
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[17px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 border-b border-gray-100 dark:border-[#172036]"
              >
                <span class="text-gray-500 dark:text-gray-400">
                  {{ lead.phone }}
                </span>
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[17px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 border-b border-gray-100 dark:border-[#172036]"
              >
                <span class="text-gray-500 dark:text-gray-400">{{ lead.source }}</span>
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[17px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 border-b border-gray-100 dark:border-[#172036]"
              >
                <span
                  class="px-[8px] py-[3px] inline-block rounded-sm font-medium text-xs"
                  :style="getStatusClass(lead.status)"
                >
                  {{ lead.status || 'Close' }}
                </span>
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[17px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 border-b border-gray-100 dark:border-[#172036]"
              >
                <div class="flex items-center">
                  <span v-if="lead.assigned_to" class="text-primary-500 dark:text-primary-400 cursor-pointer" @click="showAssignModal(lead)">
                    {{ getAssignedUserName(lead) }}
                </span>
                  <span v-else class="text-gray-500 dark:text-gray-400 cursor-pointer" @click="showAssignModal(lead)">
                    <i class="material-symbols-outlined !text-md mr-1 align-text-bottom">person_add</i>
                    Assign
                  </span>
                </div>
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[17px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 border-b border-gray-100 dark:border-[#172036]"
              >
                <div class="flex items-center gap-[9px]">
                  <button
                    type="button"
                    class="text-primary-500 leading-none custom-tooltip"
                    id="customTooltip"
                    data-text="View"
                    @click="viewLead(lead)"
                  >
                    <i class="material-symbols-outlined !text-md">
                      visibility
                    </i>
                  </button>
                  <button
                    v-if="userPermissions.is_owner || userPermissions.edit_all_leads || lead.assigned_to === currentUserId"
                    type="button"
                    class="text-gray-500 dark:text-gray-400 leading-none custom-tooltip"
                    id="customTooltip"
                    data-text="Edit"
                    @click="editLead(lead)"
                  >
                    <i class="material-symbols-outlined !text-md"> edit </i>
                  </button>
                  <button
                    v-if="(userPermissions.is_owner || userPermissions.edit_all_leads || lead.assigned_to === currentUserId) && (!lead.status || lead.status.toLowerCase() !== 'close')"
                    type="button"
                    class="text-success-500 leading-none custom-tooltip"
                    id="customTooltip"
                    data-text="Payment"
                    @click="handlePayment(lead)"
                  >
                    <i class="material-symbols-outlined !text-md"> payments </i>
                  </button>
                  <button
                    v-else-if="(userPermissions.is_owner || userPermissions.edit_all_leads || lead.assigned_to === currentUserId) && lead.status && lead.status.toLowerCase() === 'close'"
                    type="button"
                    class="text-gray-400 dark:text-gray-600 leading-none custom-tooltip cursor-not-allowed"
                    id="customTooltip"
                    data-text="Closed - Cannot Process Payment"
                  >
                    <i class="material-symbols-outlined !text-md"> payments </i>
                  </button>
                  <button
                    v-if="userPermissions.is_owner || userPermissions.edit_all_leads || lead.assigned_to === currentUserId"
                    type="button"
                    class="text-danger-500 leading-none custom-tooltip"
                    id="customTooltip"
                    data-text="Delete"
                    @click="deleteLead(lead)"
                  >
                    <i class="material-symbols-outlined !text-md"> delete </i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
                </div>

      <Pagination
        :items="itemsPerPage"
        :total="filteredLeads.length"
        :current-page="currentPage"
        @page-change="handlePageChange"
        class="px-[20px] md:px-[25px] pt-[12px] md:pt-[14px]"
                    />
                  </div>
                  </div>
  
  <!-- Delete Confirmation Modal -->
  <div v-if="showDeleteModal" class="fixed inset-0 flex items-center justify-center z-50">
    <div class="fixed inset-0 bg-black opacity-50" @click="closeDeleteModal"></div>
    <div class="bg-white dark:bg-[#0c1427] p-4 sm:p-6 rounded-lg shadow-lg w-full max-w-md mx-2 sm:mx-4 z-10 relative">
      <div class="flex justify-between items-center mb-5">
        <h3 class="text-lg font-semibold text-black dark:text-white">Confirm Deletion</h3>
                  <button
          @click="closeDeleteModal" 
          class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-2 -m-2"
        >
          <i class="material-symbols-outlined text-xl">close</i>
                  </button>
      </div>
      
      <div class="mb-6">
        <p class="text-gray-700 dark:text-gray-300 text-sm sm:text-base">
          Are you sure you want to delete this lead? This action cannot be undone.
        </p>
      </div>
      
      <div class="flex justify-between items-center pt-3">
                  <button
                    type="button"
          @click="closeDeleteModal"
          class="inline-block py-[10px] px-[20px] bg-gray-400 text-white transition-all hover:bg-gray-500 rounded-md border border-gray-400 hover:border-gray-500"
                  >
          <span class="flex items-center">
            <i class="material-symbols-outlined mr-2">close</i>
            Cancel
          </span>
                  </button>
                  <button
                    type="button"
          @click="confirmDelete"
          class="inline-block py-[10px] px-[20px] bg-danger-500 text-white transition-all hover:bg-danger-400 rounded-md border border-danger-500 hover:border-danger-400"
                  >
          <span class="flex items-center">
            <i class="material-symbols-outlined mr-2">delete</i>
            Delete
          </span>
                  </button>
                </div>
                </div>
                  </div>
  
  <!-- View Lead Modal -->
  <div v-if="showViewModal" class="fixed inset-0 flex items-center justify-center z-50">
    <div class="fixed inset-0 bg-black opacity-50" @click="closeViewModal"></div>
    <div class="bg-white dark:bg-[#0c1427] p-3 sm:p-6 rounded-lg shadow-lg w-full max-w-lg mx-2 sm:mx-4 z-10 relative overflow-y-auto max-h-[90vh]">
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-lg font-medium text-black dark:text-white">Lead Details</h3>
                  <button
          @click="closeViewModal" 
          class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-2 -m-2"
        >
          <i class="material-symbols-outlined text-xl">close</i>
                  </button>
                </div>
      
      <div v-if="leadToView" class="mb-4 space-y-3 sm:space-y-4">
        <div class="flex flex-col sm:flex-row sm:items-center mb-3 sm:mb-4">
          <img :src="getLeadImageUrl(leadToView)" class="w-14 h-14 sm:w-16 sm:h-16 rounded-full mb-2 sm:mb-0 sm:mr-4" :alt="leadToView.name">
          <div>
            <h4 class="text-lg sm:text-xl font-medium text-black dark:text-white">{{ leadToView.name }}</h4>
            <p class="text-gray-500 dark:text-gray-400">ID: {{ getShortId(leadToView.id) }}</p>
                </div>
                  </div>
      
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div>
            <p class="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Email</p>
            <p class="text-black dark:text-white break-words">{{ leadToView.email }}</p>
                  </div>
          <div>
            <p class="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Phone</p>
            <p class="text-black dark:text-white">{{ leadToView.phone || 'N/A' }}</p>
                </div>
          <div>
            <p class="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Company</p>
            <p class="text-black dark:text-white">{{ getCompanyName(leadToView) }}</p>
          </div>
          <div>
            <p class="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Source</p>
            <p class="text-black dark:text-white">{{ leadToView.source }}</p>
          </div>
          <div>
            <p class="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Status</p>
            <p>
                <span
                class="px-[8px] py-[3px] inline-block rounded-sm font-medium text-xs"
                :style="getStatusClass(leadToView.status)"
                >
                {{ leadToView.status || 'Close' }}
                </span>
            </p>
                </div>
          <div>
            <p class="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Created Date</p>
            <p class="text-black dark:text-white">{{ formatDate(leadToView.created_at) }}</p>
                </div>
                  </div>
        
        <div v-if="leadToView.notes">
          <p class="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Notes</p>
          <p class="text-black dark:text-white whitespace-pre-line break-words">{{ leadToView.notes }}</p>
                  </div>
                </div>
      
      <div class="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
          @click="closeViewModal"
          class="px-3 py-2 sm:px-4 text-sm bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-md transition-all dark:bg-[#172036] dark:text-gray-200 dark:hover:bg-[#1e2746]"
                  >
          Close
                  </button>
                  <button
                    type="button"
          @click="editLead(leadToView)"
          class="px-3 py-2 sm:px-4 text-sm bg-primary-500 hover:bg-primary-600 text-white rounded-md transition-all"
                  >
          Edit
                  </button>
      </div>
    </div>
  </div>
  
  <!-- Edit Lead Modal -->
  <div v-if="showEditModal" class="fixed inset-0 flex items-center justify-center z-50">
    <div class="fixed inset-0 bg-black opacity-50" @click="closeEditModal"></div>
    <div class="bg-white dark:bg-[#0c1427] p-3 sm:p-6 rounded-lg shadow-lg w-full max-w-lg mx-2 sm:mx-4 z-10 relative overflow-y-auto max-h-[90vh]">
      <div class="flex justify-between items-center mb-3 sm:mb-4">
        <h3 class="text-lg font-medium text-black dark:text-white">Edit Lead</h3>
                  <button
          @click="closeEditModal" 
          class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-2 -m-2"
        >
          <i class="material-symbols-outlined text-xl">close</i>
                  </button>
                </div>
      
      <div v-if="leadToEdit" class="mb-4 sm:mb-6">
        <form @submit.prevent="updateLead" class="space-y-3 sm:space-y-4">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label class="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
              <input 
                type="text" 
                v-model="leadToEdit.name" 
                required
                class="h-[40px] sm:h-[45px] rounded-md text-black dark:text-white border border-gray-200 dark:border-[#172036] bg-white dark:bg-[#0c1427] px-[14px] sm:px-[17px] block w-full outline-0 transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:border-primary-500 text-sm"
              />
                </div>
            <div>
              <label class="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
              <input 
                type="email" 
                v-model="leadToEdit.email" 
                required
                class="h-[40px] sm:h-[45px] rounded-md text-black dark:text-white border border-gray-200 dark:border-[#172036] bg-white dark:bg-[#0c1427] px-[14px] sm:px-[17px] block w-full outline-0 transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:border-primary-500 text-sm"
                    />
                  </div>
            <div>
              <label class="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
              <input 
                type="text" 
                v-model="leadToEdit.phone"
                class="h-[40px] sm:h-[45px] rounded-md text-black dark:text-white border border-gray-200 dark:border-[#172036] bg-white dark:bg-[#0c1427] px-[14px] sm:px-[17px] block w-full outline-0 transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:border-primary-500 text-sm"
              />
                  </div>
            <div>
              <label class="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
              <select 
                v-model="leadToEdit.status"
                required
                class="h-[40px] sm:h-[45px] rounded-md text-black dark:text-white border border-gray-200 dark:border-[#172036] bg-white dark:bg-[#0c1427] px-[12px] sm:px-[14px] block w-full outline-0 cursor-pointer transition-all focus:border-primary-500 text-sm"
              >
                <option value="" disabled>Select</option>
                <option v-for="status in statuses" :key="status.id" :value="status.name">{{ status.name }}</option>
              </select>
                </div>
            <div>
              <label class="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Source</label>
              <select 
                v-model="leadToEdit.source"
                required
                class="h-[40px] sm:h-[45px] rounded-md text-black dark:text-white border border-gray-200 dark:border-[#172036] bg-white dark:bg-[#0c1427] px-[12px] sm:px-[14px] block w-full outline-0 cursor-pointer transition-all focus:border-primary-500 text-sm"
              >
                <option value="" disabled>Select</option>
                <option v-for="source in sources" :key="source.id" :value="source.name">{{ source.name }}</option>
              </select>
                </div>
            <!-- Income field - only shown when status is Close -->
            <div v-if="leadToEdit.status && leadToEdit.status.toLowerCase() === 'close'">
              <label class="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Income</label>
              <div class="relative">
                <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input 
                  type="number" 
                  v-model="leadToEdit.dealamount"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  class="h-[40px] sm:h-[45px] rounded-md text-black dark:text-white border border-gray-200 dark:border-[#172036] bg-white dark:bg-[#0c1427] pl-7 pr-[14px] sm:pr-[17px] block w-full outline-0 transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:border-primary-500 text-sm"
                    />
                  </div>
                  </div>
                </div>
          
          <!-- Custom Fields Section -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 pt-2 border-t border-gray-200 dark:border-[#172036] mt-4">
            <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300 sm:col-span-2 mb-2">Custom Fields</h4>
            
            <template v-for="field in customFields.filter(f => f.required)" :key="field.key">
              <div class="mb-1" :class="{'sm:col-span-2': field.type === 'textarea'}">
                <label class="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {{ field.label }} {{ field.required ? '*' : '' }}
                </label>
                
                <!-- Text, number, email, phone inputs -->
                <input 
                  v-if="getInputType(field.type) !== 'select' && field.type !== 'textarea'"
                  :type="getInputType(field.type)"
                  v-model="customFieldValues[field.key]"
                  :required="field.required"
                  class="h-[40px] sm:h-[45px] rounded-md text-black dark:text-white border border-gray-200 dark:border-[#172036] bg-white dark:bg-[#0c1427] px-[14px] sm:px-[17px] block w-full outline-0 transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:border-primary-500 text-sm"
                />
                
                <!-- Textarea for text area fields -->
                <textarea 
                  v-else-if="field.type === 'textarea'"
                  v-model="customFieldValues[field.key]"
                  :required="field.required"
                  rows="3"
                  class="rounded-md text-black dark:text-white border border-gray-200 dark:border-[#172036] bg-white dark:bg-[#0c1427] px-[14px] sm:px-[17px] py-[10px] sm:py-[12px] block w-full outline-0 transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:border-primary-500 text-sm"
                ></textarea>
                
                <!-- Dropdown for select fields -->
                <select
                  v-else
                  v-model="customFieldValues[field.key]"
                  :required="field.required"
                  class="h-[40px] sm:h-[45px] rounded-md text-black dark:text-white border border-gray-200 dark:border-[#172036] bg-white dark:bg-[#0c1427] px-[12px] sm:px-[14px] block w-full outline-0 cursor-pointer transition-all focus:border-primary-500 text-sm"
                >
                  <option value="">Select</option>
                  <option v-for="option in field.options" :key="option" :value="option">{{ option }}</option>
                </select>
              </div>
            </template>
          </div>
          
          <div>
            <label class="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
            <textarea 
              v-model="leadToEdit.notes"
              rows="3"
              class="rounded-md text-black dark:text-white border border-gray-200 dark:border-[#172036] bg-white dark:bg-[#0c1427] px-[14px] sm:px-[17px] py-[10px] sm:py-[12px] block w-full outline-0 transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:border-primary-500 text-sm"
            ></textarea>
          </div>
          
          <div class="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
              @click="closeEditModal"
              class="px-3 py-2 sm:px-4 text-sm bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-md transition-all dark:bg-[#172036] dark:text-gray-200 dark:hover:bg-[#1e2746]"
                  >
              Cancel
                  </button>
                  <button
              type="submit" 
              class="px-3 py-2 sm:px-4 text-sm bg-primary-500 hover:bg-primary-600 text-white rounded-md transition-all"
            >
              Save Changes
                  </button>
                </div>
        </form>
                </div>
    </div>
  </div>
  
  <!-- Toast Notification -->
  <div 
    v-if="toastMessage" 
    class="fixed bottom-4 right-4 left-4 md:left-auto md:max-w-md p-4 rounded-md shadow-lg transform transition-transform duration-300 z-50 flex items-center justify-between"
    :class="{ 
      'bg-green-600 text-white': toastType === 'success', 
      'bg-red-600 text-white': toastType === 'error', 
      'bg-blue-600 text-white': toastType === 'info' 
    }"
  >
    <span>{{ toastMessage }}</span>
    <button @click="closeToast" class="ml-4 text-white hover:text-gray-300">×</button>
                  </div>

  <!-- Assign Modal -->
  <div v-if="showingAssignModal" class="fixed inset-0 flex items-center justify-center z-50">
    <div class="fixed inset-0 bg-black opacity-50" @click="closeAssignModal"></div>
    <div class="bg-white dark:bg-[#0c1427] p-4 sm:p-6 rounded-lg shadow-lg w-full max-w-md mx-2 sm:mx-4 z-10 relative">
      <div class="flex justify-between items-center mb-5">
        <h3 class="text-lg font-semibold text-black dark:text-white">
          {{ isBulkAssign ? `Assign ${selectedLeads.length} Selected Leads` : 'Assign Lead' }}
        </h3>
        <button
          @click="closeAssignModal" 
          class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-2 -m-2"
        >
          <i class="material-symbols-outlined text-xl">close</i>
        </button>
      </div>
      
      <div class="mb-4">
        <p class="text-gray-700 dark:text-gray-300 mb-4">
          {{ isBulkAssign ? 'Select a user to assign these leads to:' : `Assigning lead: ${leadToAssign?.name || ''}` }}
        </p>
        
        <div v-if="loadingUsers" class="text-center p-4">
          <div class="inline-block animate-spin rounded-full border-4 border-primary-500 border-t-transparent h-8 w-8"></div>
        </div>
        
        <div v-else class="max-h-[200px] overflow-y-auto border dark:border-[#172036] rounded-md">
          <div 
            v-for="user in users" 
            :key="user.user_id"
            @click="isBulkAssign ? assignSelectedLeads(user.user_id) : assignLead(user.user_id)"
            class="p-3 hover:bg-gray-100 dark:hover:bg-[#172036] cursor-pointer border-b last:border-b-0 dark:border-[#172036] transition-colors flex items-center"
          >
            <div class="mr-3 w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-white font-medium text-sm">
              {{ user.display_name ? user.display_name.substring(0, 1).toUpperCase() : user.email.substring(0, 1).toUpperCase() }}
            </div>
            <div>
              <p class="font-medium text-black dark:text-white">{{ user.display_name || user.email }}</p>
              <p class="text-xs text-gray-500 dark:text-gray-400">{{ user.email }}</p>
            </div>
          </div>
        </div>
      </div>
      
      <div class="flex justify-end gap-3">
        <button
          type="button"
          @click="closeAssignModal"
          class="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md transition-all dark:bg-[#172036] dark:text-gray-200 dark:hover:bg-[#1e2746]"
        >
          Cancel
        </button>
        <button
          v-if="currentUserId && !isBulkAssign"
          type="button"
          @click="assignLead(null)"
          class="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-md transition-all"
        >
          Unassign
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed, watch, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import { supabase } from "@/lib/supabaseClient";
import stateStore from "@/utils/store";
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";
import RevenueGrowthChart from "./RevenueGrowthChart.vue";
import LeadConversionChart from "./LeadConversionChart.vue";
import TotalOrdersChart from "./TotalOrdersChart.vue";
import AnnualProfitChart from "./AnnualProfitChart.vue";
import Pagination from "@/components/Common/Pagination.vue";
import { createToast } from "@/utils/toast";
import { getCurrentOrgId } from "@/utils/getCurrentOrgId";

interface Lead {
  id: string;
  name: string;
  email: string;
  phone: string;
  status: string;
  source: string;
  notes: string;
  data: any;
  created_by: string;
  assigned_to: string | null;
  org_id: string;
  created_at: string;
  updated_at: string;
  dealamount?: number;
  dealtime?: string;
}

interface Status {
  id: string;
  name: string;
  color: string;
}

interface Source {
  id: string;
  name: string;
}

interface UserPermissions {
  is_owner?: boolean;
  edit_all_leads?: boolean;
  view_all_leads?: boolean;
  can_invite_users?: boolean;
  [key: string]: any;
}

interface CustomField {
  key: string;
  label: string;
  type: string;
  required: boolean;
  options?: string[];
}

// Type definition for nested translation objects
type TranslationObject = { [key: string]: string | TranslationObject };

export default defineComponent({
  // eslint-disable-next-line vue/multi-word-component-names
  name: "Leads",
  components: {
    RevenueGrowthChart,
    LeadConversionChart,
    TotalOrdersChart,
    AnnualProfitChart,
    Pagination,
  },
  setup() {
    const router = useRouter();
    const stateStoreInstance = stateStore;
    const loading = ref(true);
    const organizationId = ref<string | null>(null);
    const searchQuery = ref('');
    const currentPage = ref(1);
    const itemsPerPage = ref(10);
    const selectedLeads = ref<string[]>([]);
    const selectAll = ref(false);
    
    // Add the missing leads ref declaration
    const leads = ref<Lead[]>([]);
    
    // User permissions
    const currentUserId = ref<string | null>(null);
    const userPermissions = ref<UserPermissions>({
      is_owner: false,
      edit_all_leads: false,
      view_all_leads: false,
      can_invite_users: false
    });
    
    // i18n setup
    const { locale } = useI18n();
    
    // Store direct references to translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string) => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return enResult;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return result;
    };
    
    // Delete modal
    const showDeleteModal = ref(false);
    const leadToDelete = ref<Lead | null>(null);
    
    // Edit modal
    const showEditModal = ref(false);
    const leadToEdit = ref<Lead | null>(null);
    
    // View modal
    const showViewModal = ref(false);
    const leadToView = ref<Lead | null>(null);
    
    // Toast notification
    const toastMessage = ref('');
    const toastType = ref<'success' | 'error' | 'info'>('info');
    const toastTimeout = ref<number | null>(null);
    
    const statuses = ref<Status[]>([]);
    const sources = ref<Source[]>([]);
    
    // Store for organization users
    const users = ref<{ user_id: string; display_name: string; email: string }[]>([]);
    const loadingUsers = ref(false);
    
    // Lead assignment
    const showingAssignModal = ref(false);
    const leadToAssign = ref<Lead | null>(null);
    const isBulkAssign = ref(false);
    
    // Custom fields
    const customFields = ref<CustomField[]>([]);
    const customFieldValues = ref<Record<string, string>>({});
    const loadingCustomFields = ref(false);
    
    const filteredLeads = computed(() => {
      if (!searchQuery.value) return leads.value;
      
      const query = searchQuery.value.toLowerCase();
      return leads.value.filter(lead => 
        lead.name.toLowerCase().includes(query) ||
        lead.email.toLowerCase().includes(query) ||
        (lead.phone && lead.phone.toLowerCase().includes(query)) ||
        (lead.status && lead.status.toLowerCase().includes(query)) ||
        (lead.source && lead.source.toLowerCase().includes(query)) ||
        (lead.notes && lead.notes.toLowerCase().includes(query))
      );
    });
    
    const paginatedLeads = computed(() => {
      const startIndex = (currentPage.value - 1) * itemsPerPage.value;
      const endIndex = startIndex + itemsPerPage.value;
      return filteredLeads.value.slice(startIndex, endIndex);
    });
    
    // Reset pagination when search changes
    watch(searchQuery, () => {
      currentPage.value = 1;
    });
    
    // Fetch leads from the database
    const fetchLeads = async () => {
      try {
        loading.value = true;
        
        // Get current user info
        const { data: userData, error: userError } = await supabase.auth.getUser();
        if (userError) throw userError;
        
        if (!userData.user) {
          createToast('User is not authenticated', 'error');
          loading.value = false;
          return;
        }
        
        currentUserId.value = userData.user.id;
        
        // Get user profile with organization ID and permissions
        const { data: profileData, error: profileError } = await supabase
          .from('user_profiles')
          .select('org_id, permissions_json')
          .eq('user_id', currentUserId.value)
          .single();
          
        if (profileError) throw profileError;
        
        if (!profileData || !profileData.org_id) {
          createToast('User is not associated with an organization', 'error');
          loading.value = false;
          return;
        }
        
        // Store organization ID with proper type assertion
        organizationId.value = profileData.org_id as string;
        
        // Fetch users first to ensure they're available when displaying leads
        await fetchUsers();
        
        // Store permissions for use throughout the component
        const permissions = profileData?.permissions_json as UserPermissions || {};
        userPermissions.value = {
          is_owner: permissions.is_owner === true,
          edit_all_leads: permissions.edit_all_leads === true,
          view_all_leads: permissions.view_all_leads === true,
          can_invite_users: permissions.can_invite_users === true
        };
        
        // Build query based on permissions
        let query = supabase
          .from('leads')
          .select('*')
          .eq('org_id', organizationId.value);
          
        // If user can't view all leads, only show assigned leads
        if (!userPermissions.value.is_owner && !userPermissions.value.view_all_leads) {
          query = query.eq('assigned_to', currentUserId.value);
        }
        
        // Execute query
        const { data, error } = await query.order('created_at', { ascending: false });
        
        if (error) throw error;
        
        // Fix type conversion by using unknown as intermediate type
        leads.value = (data || []) as unknown as Lead[];
        
        if (leads.value.length > 0) {
          createToast(`Successfully loaded ${leads.value.length} leads from database`, 'success');
        } else {
          if (!userPermissions.value.view_all_leads && !userPermissions.value.is_owner) {
            createToast('No leads assigned to you found', 'info');
          } else {
            createToast('No leads found. Create your first lead by clicking "Add New Lead"', 'info');
          }
        }
      } catch (error) {
        console.error('Error fetching leads:', error);
        createToast('Failed to load leads', 'error');
      } finally {
        loading.value = false;
      }
    };
    
    // Fetch status and source options
    const fetchOptions = async () => {
      try {
        // Check if organization ID is available
        if (!organizationId.value) {
          // Get current user
          const { data: userData, error: userError } = await supabase.auth.getUser();
          if (userError) throw userError;
          
          if (!userData.user) {
            console.warn('Cannot fetch options: User not authenticated');
            return;
          }
          
          const userId = userData.user.id;
          
          // Get organization ID from user profile
          const { data: profileData, error: profileError } = await supabase
            .from('user_profiles')
            .select('org_id')
            .eq('user_id', userId)
            .single();
            
          if (profileError) {
            console.error('Error fetching user profile:', profileError);
            return;
          }
          
          if (!profileData || !profileData.org_id) {
            console.warn('Cannot fetch options: User profile has no organization ID');
            return;
          }
          
          // Update organization ID with proper type assertion
          organizationId.value = profileData.org_id as string;
        }
        
        if (!organizationId.value) {
          console.warn('Cannot fetch options: Missing organization ID');
          return;
        }
        
        // Fetch statuses
        const { data: statusData, error: statusError } = await supabase
          .from('statusleads')
          .select('*')
          .eq('organization_id', organizationId.value);
        
        if (statusError) throw statusError;
        
        statuses.value = (statusData || []).map((item: any) => ({
          id: item.id,
          name: item.status,
          color: item.color || '#605DFF'
        }));
        
        // Make sure "Close" is included in the status options
        if (!statuses.value.find(s => s.name.toLowerCase() === 'close')) {
          statuses.value.push({
            id: 'close-default',
            name: 'Close',
            color: '#10B981' // Green color for close status
          });
        }
        
        // Fetch sources
        const { data: sourceData, error: sourceError } = await supabase
          .from('leadsources')
          .select('*')
          .eq('organization_id', organizationId.value);
        
        if (sourceError) throw sourceError;
        
        sources.value = (sourceData || []).map((item: any) => ({
          id: item.id,
          name: item.source
        }));
        
        // Fetch custom fields
        await fetchCustomFields();
      } catch (error) {
        console.error('Error fetching options:', error);
        createToast('Failed to fetch options', 'error');
      }
    };
    
    // Get appropriate input type based on custom field type
    const getInputType = (fieldType: string) => {
      switch (fieldType) {
        case 'number': return 'number';
        case 'email': return 'email';
        case 'phone': return 'tel';
        case 'date': return 'date';
        case 'checkbox': return 'checkbox';
        case 'select': return 'select';
        default: return 'text';
      }
    };
    
    // Fetch custom fields
    const fetchCustomFields = async () => {
      try {
        if (!organizationId.value) {
          console.warn('Cannot fetch custom fields: Missing organization ID');
          return;
        }
        
        loadingCustomFields.value = true;
        
        // Get custom fields definition
        const { data, error } = await supabase
          .from('customleadsfield')
          .select('id, content')
          .eq('organizationid', organizationId.value);
        
        if (error) throw error;
        
        if (!data || data.length === 0) {
          // No custom fields defined
          customFields.value = [];
          return;
        }
        
        // Reset the custom fields array
        customFields.value = [];
        
        // Process all records
        for (const record of data) {
          if (record && record.content) {
            // Add type assertion to content
            const content = record.content as any[];
            
            if (Array.isArray(content) && content.length > 0) {
              // Process each field in the content array
              for (const field of content) {
                if (field) {
                  (field as any).customFieldsId = record.id;
                  customFields.value.push(field as CustomField);
                  
                  // Initialize field value
                  customFieldValues.value[field.key] = '';
                }
              }
            }
          }
        }
        
        console.log('Custom fields loaded:', customFields.value);
      } catch (error) {
        console.error('Error fetching custom fields:', error);
        createToast('Failed to load custom fields', 'error');
      } finally {
        loadingCustomFields.value = false;
      }
    };
    
    // Fetch organization users
    const fetchUsers = async () => {
      try {
        loadingUsers.value = true;
        
        // Check if organization ID is available
        if (!organizationId.value) {
          console.warn('Cannot fetch users: Missing organization ID');
          loadingUsers.value = false;
          return;
        }
        
        const { data, error } = await supabase
          .from('user_profiles')
          .select('user_id, display_name, email')
          .eq('org_id', organizationId.value);
          
        if (error) throw error;
        
        users.value = (data || []) as { user_id: string; display_name: string; email: string }[];
      } catch (error) {
        console.error('Error fetching users:', error);
        createToast('Failed to load users', 'error');
      } finally {
        loadingUsers.value = false;
      }
    };
    
    // Format date
    const formatDate = (dateString: string) => {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        day: 'numeric', 
        month: 'short', 
        year: 'numeric' 
      });
    };
    
    // Get shortened ID for display
    const getShortId = (id: string) => {
      return `#${id.substring(0, 4)}`;
    };
    
    // Get lead image URL
    const getLeadImageUrl = (lead: Lead) => {
      // Check if lead has a profile image in data.image
      if (lead.data && lead.data.image) {
        return lead.data.image;
      }
      
      // Generate a placeholder image based on lead ID
      const imageIndex = parseInt(lead.id.substring(0, 2), 16) % 15 + 1;
      return require(`@/assets/images/users/user${imageIndex}.jpg`);
    };
    
    // Get company name from lead data
    const getCompanyName = (lead: Lead) => {
      if (lead.data && lead.data.company) {
        return lead.data.company;
      }
      return "N/A";
    };
    
    // Get assigned user name
    const getAssignedUserName = (lead: Lead) => {
      if (!lead.assigned_to) return 'Unassigned';
      
      // Look up the user in users array
      const assignedUser = users.value.find(user => user.user_id === lead.assigned_to);
      return assignedUser ? assignedUser.display_name || assignedUser.email : 'Unknown User';
    };
    
    // Show the assign modal
    const showAssignModal = (lead: Lead) => {
      leadToAssign.value = lead;
      showingAssignModal.value = true;
      isBulkAssign.value = false;
      
      // Ensure users are loaded
      if (users.value.length === 0) {
        fetchUsers();
      }
    };
    
    // Show bulk assign modal for multiple leads
    const showBulkAssignModal = () => {
      if (selectedLeads.value.length === 0) return;
      
      leadToAssign.value = null;
      showingAssignModal.value = true;
      isBulkAssign.value = true;
      
      // Ensure users are loaded
      if (users.value.length === 0) {
        fetchUsers();
      }
    };
    
    // Close the assign modal
    const closeAssignModal = () => {
      showingAssignModal.value = false;
      leadToAssign.value = null;
      isBulkAssign.value = false;
    };
    
    // Assign multiple leads to a user
    const assignSelectedLeads = async (userId: string) => {
      if (selectedLeads.value.length === 0) return;
      
      try {
        loading.value = true;
        
        // תאריך העדכון יהיה זהה לכל השיוכים
        const updateTimestamp = new Date().toISOString();
        
        // עדכון כל אחד מהרשומות שנבחרו
        const { error } = await supabase
          .from('leads')
          .update({
            assigned_to: userId,
            updated_at: updateTimestamp
          })
          .in('id', selectedLeads.value);
          
        if (error) throw error;
        
        // עדכון המידע בתצוגה המקומית
        leads.value.forEach(lead => {
          if (selectedLeads.value.includes(lead.id)) {
            lead.assigned_to = userId;
            lead.updated_at = updateTimestamp;
          }
        });
        
        createToast(`${selectedLeads.value.length} leads assigned successfully`, 'success');
        closeAssignModal();
      } catch (error) {
        console.error('Error assigning leads:', error);
        createToast('Failed to assign leads', 'error');
      } finally {
        loading.value = false;
      }
    };
    
    // Assign lead to a user
    const assignLead = async (userId: string | null) => {
      if (!leadToAssign.value) return;
      
      try {
        loading.value = true;
        
        const { error } = await supabase
          .from('leads')
          .update({
            assigned_to: userId,
            updated_at: new Date().toISOString()
          })
          .eq('id', leadToAssign.value.id);
          
        if (error) throw error;
        
        // Update the lead in the local array
        const leadIndex = leads.value.findIndex(l => l.id === leadToAssign.value?.id);
        if (leadIndex >= 0) {
          leads.value[leadIndex].assigned_to = userId;
          leads.value[leadIndex].updated_at = new Date().toISOString();
        }
        
        createToast('Lead assigned successfully', 'success');
        closeAssignModal();
      } catch (error) {
        console.error('Error assigning lead:', error);
        createToast('Failed to assign lead', 'error');
      } finally {
        loading.value = false;
      }
    };
    
    // Get status class for styling
    const getStatusClass = (status: string) => {
      if (!status) {
        // Default styling for no status
        return {
          backgroundColor: 'rgba(16, 185, 129, 0.15)', // Light green background
          color: '#10B981', // Green text
        };
      }
      
      // First, check if we have this status in our statuses array
      const statusObj = statuses.value.find(s => s.name.toLowerCase() === status.toLowerCase());
      
      if (statusObj && statusObj.color) {
        // Use the status's color from the database
        const colorHex = statusObj.color;
        
        // Convert hex to RGB for generating lighter background colors
        const r = parseInt(colorHex.slice(1, 3), 16);
        const g = parseInt(colorHex.slice(3, 5), 16);
        const b = parseInt(colorHex.slice(5, 7), 16);
        
        // Create a light background color with 15% opacity for light mode
        const bgColor = `rgba(${r}, ${g}, ${b}, 0.15)`;
        
        // Return inline styles for both background and text color
        return {
          backgroundColor: bgColor,
          color: colorHex
        };
      }
      
      // Fallback to the old hardcoded styles if status not found in database
      switch (status.toLowerCase()) {
        case 'new':
        case 'confirmed':
          return {
            backgroundColor: 'rgba(96, 93, 255, 0.15)',
            color: '#605DFF'
          };
        case 'in progress':
        case 'qualified': 
        case 'close':
          return {
            backgroundColor: 'rgba(16, 185, 129, 0.15)',
            color: '#10B981'
          };
        case 'pending':
          return {
            backgroundColor: 'rgba(245, 158, 11, 0.15)',
            color: '#F59E0B'
          };
        case 'rejected':
        case 'closed lost':
          return {
            backgroundColor: 'rgba(239, 68, 68, 0.15)',
            color: '#EF4444'
          };
        case 'closed won':
          return {
            backgroundColor: 'rgba(14, 165, 233, 0.15)',
            color: '#0EA5E9'
          };
        default:
          return {
            backgroundColor: 'rgba(156, 163, 175, 0.15)',
            color: '#9CA3AF'
          };
      }
    };
    
    // Toggle select all
    const toggleSelectAll = () => {
      if (selectAll.value) {
        selectedLeads.value = paginatedLeads.value.map(lead => lead.id);
      } else {
        selectedLeads.value = [];
      }
    };
    
    // Handle pagination
    const handlePageChange = (page: number) => {
      currentPage.value = page;
    };
    
    // Handle search
    const handleSearch = () => {
      currentPage.value = 1;
    };
    
    // View lead details
    const viewLead = (lead: Lead) => {
      router.push(`/crm/leads/${lead.id}`);
    };
    
    // Edit lead
    const editLead = (lead: Lead) => {
      // Create a deep copy to avoid mutation issues
      leadToEdit.value = JSON.parse(JSON.stringify(lead));
      
      // Initialize dealamount field if it doesn't exist
      if (leadToEdit.value && !leadToEdit.value.dealamount) {
        leadToEdit.value.dealamount = 0;
      }
      
      // Ensure options and custom fields are loaded
      if (statuses.value.length === 0 || sources.value.length === 0) {
        fetchOptions();
      }
      
      // Fetch custom field values for this lead
      fetchCustomFieldValues(lead.id);
      
      showEditModal.value = true;
    };
    
    // Fetch custom field values for a specific lead
    const fetchCustomFieldValues = async (leadId: string) => {
      try {
        if (!leadId || customFields.value.length === 0) return;
        
        // Clear previous values
        for (const field of customFields.value) {
          customFieldValues.value[field.key] = '';
        }
        
        // Get all custom field values for this lead
        const { data, error } = await supabase
          .from('customleadsfieldcontent')
          .select('*')
          .eq('leadid', leadId);
        
        if (error) throw error;
        
        // Set values based on database records
        if (data && data.length > 0) {
          for (const record of data) {
            // Find matching field
            const matchingField = customFields.value.find(
              field => (field as any).customFieldsId === record.customleadsfieldid &&
                      (field.key === record.fieldkey || (!record.fieldkey && record.customleadsfieldid === field.key))
            );
            
            if (matchingField) {
              customFieldValues.value[matchingField.key] = (record.content as string) || '';
            }
          }
        }
      } catch (error) {
        console.error('Error fetching custom field values:', error);
      }
    };
    
    // Save custom field values for a lead
    const saveCustomFieldValues = async (leadId: string) => {
      try {
        if (!leadId || customFields.value.length === 0) return;
        
        // Get existing custom field values for this lead
        const { data: existingValues, error: queryError } = await supabase
          .from('customleadsfieldcontent')
          .select('*')
          .eq('leadid', leadId);
          
        if (queryError) {
          console.error('Error fetching existing custom field values:', queryError);
          return;
        }
        
        // Process each custom field
        for (const field of customFields.value) {
          if (!field.required) continue; // Skip fields that aren't required (since we're only showing required fields)
          
          const value = customFieldValues.value[field.key];
          const customFieldsId = (field as any).customFieldsId;
          
          if (!customFieldsId) {
            console.error('Missing customFieldsId for field:', field.key);
            continue;
          }
          
          // Look for existing record for this field
          const existingRecord = existingValues?.find(
            record => record.customleadsfieldid === customFieldsId && 
                    (record.fieldkey === field.key || (!record.fieldkey && record.customleadsfieldid === field.key))
          );
          
          if (existingRecord) {
            // Update existing record
            const { error: updateError } = await supabase
              .from('customleadsfieldcontent')
              .update({ 
                content: value, 
                updated_at: new Date().toISOString() 
              })
              .eq('id', existingRecord.id);
              
            if (updateError) {
              console.error('Error updating custom field:', updateError);
            }
          } else if (value !== undefined && value !== null && value !== '') {
            // Insert new record
            const { error: insertError } = await supabase
              .from('customleadsfieldcontent')
              .insert({
                leadid: leadId,
                customleadsfieldid: customFieldsId,
                fieldkey: field.key,
                content: value,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              });
              
            if (insertError) {
              console.error('Error inserting custom field:', insertError);
            }
          }
        }
      } catch (error) {
        console.error('Error saving custom field values:', error);
      }
    };
    
    // Handle payment for lead
    const handlePayment = (lead: Lead) => {
      // For now, just show a toast notification
      createToast(`Processing payment for: ${lead.name}`, 'info');
      // In the future, this could open a payment modal or navigate to a payment page
    };
    
    // Delete lead
    const deleteLead = (lead: Lead) => {
      leadToDelete.value = lead;
      showDeleteModal.value = true;
    };
    
    // Close delete modal
    const closeDeleteModal = () => {
      showDeleteModal.value = false;
      leadToDelete.value = null;
    };
    
    // Close edit modal
    const closeEditModal = () => {
      showEditModal.value = false;
      leadToEdit.value = null;
    };
    
    // Close view modal
    const closeViewModal = () => {
      showViewModal.value = false;
      leadToView.value = null;
    };
    
    // Confirm delete
    const confirmDelete = async () => {
      if (!leadToDelete.value) return;
      
      try {
        const { error } = await supabase
          .from('leads')
          .delete()
          .eq('id', leadToDelete.value.id);
        
        if (error) throw error;
        
        // Remove from local list
        leads.value = leads.value.filter(lead => lead.id !== leadToDelete.value?.id);
        
        // Update selected leads
        selectedLeads.value = selectedLeads.value.filter(id => id !== leadToDelete.value?.id);
        
        createToast('Lead deleted successfully', 'success');
      } catch (error) {
        console.error('Error deleting lead:', error);
        createToast('Failed to delete lead', 'error');
      } finally {
        closeDeleteModal();
      }
    };
    
    // Update lead
    const updateLead = async () => {
      if (!leadToEdit.value) return;
      
      try {
        // Handle special case for Close status if it doesn't exist in database
        if (leadToEdit.value.status === 'Close') {
          // Check if Close status exists in statusleads table
          const { data: closeStatusData, error: closeStatusError } = await supabase
            .from('statusleads')
            .select('*')
            .eq('organization_id', organizationId.value)
            .eq('status', 'Close')
            .maybeSingle();
          
          if (closeStatusError) throw closeStatusError;
          
          // If Close status doesn't exist, create it
          if (!closeStatusData) {
            await supabase
              .from('statusleads')
              .insert({
                status: 'Close',
                color: '#10B981', // Green color for close status
                organization_id: organizationId.value
              });
          }
          
          // Set dealtime to now for Closed leads (for charts and analytics)
          leadToEdit.value.dealtime = new Date().toISOString();
        }
        
        // Update timestamp
        leadToEdit.value.updated_at = new Date().toISOString();
        
        const { error } = await supabase
          .from('leads')
          .update({
            name: leadToEdit.value.name,
            email: leadToEdit.value.email,
            phone: leadToEdit.value.phone,
            status: leadToEdit.value.status,
            source: leadToEdit.value.source,
            notes: leadToEdit.value.notes,
            data: leadToEdit.value.data,
            updated_at: leadToEdit.value.updated_at,
            dealamount: leadToEdit.value.dealamount,
            dealtime: leadToEdit.value.dealtime
          })
          .eq('id', leadToEdit.value.id);
        
        if (error) throw error;
        
        // Update in local list
        const index = leads.value.findIndex(lead => lead.id === leadToEdit.value?.id);
        if (index !== -1) {
          leads.value[index] = { ...leadToEdit.value };
        }
        
        // Save custom field values
        await saveCustomFieldValues(leadToEdit.value.id);
        
        createToast('Lead updated successfully', 'success');
        closeEditModal();
      } catch (error) {
        console.error('Error updating lead:', error);
        createToast('Failed to update lead', 'error');
      }
    };
    
    // Toast functions
    const createToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
      toastMessage.value = message;
      toastType.value = type;
      
      if (toastTimeout.value) {
        clearTimeout(toastTimeout.value);
      }
      
      toastTimeout.value = window.setTimeout(() => {
        closeToast();
      }, 3000);
    };
    
    const closeToast = () => {
      toastMessage.value = '';
      if (toastTimeout.value) {
        clearTimeout(toastTimeout.value);
        toastTimeout.value = null;
      }
    };
    
    // Setup event listeners
    onMounted(() => {
      // Listen for refresh events from the AddNewLeadPopup component
      window.addEventListener('refreshLeads', fetchLeads);
      
      // Initial fetch
      fetchLeads();
      fetchOptions();
      
      // Cleanup event listeners
      onBeforeUnmount(() => {
        window.removeEventListener('refreshLeads', fetchLeads);
        if (toastTimeout.value) {
          clearTimeout(toastTimeout.value);
        }
      });
    });

    return {
      router,
      stateStoreInstance,
      leads,
      loading,
      searchQuery,
      currentPage,
      itemsPerPage,
      selectedLeads,
      selectAll,
      filteredLeads,
      paginatedLeads,
      showDeleteModal,
      toastMessage,
      toastType,
      showEditModal,
      showViewModal,
      leadToDelete,
      leadToEdit,
      leadToView,
      leadToAssign,
      showingAssignModal,
      loadingUsers,
      users,
      statuses,
      sources,
      currentUserId,
      userPermissions,
      customFields,
      customFieldValues,
      getInputType,
      editLead,
      handlePayment,
      deleteLead,
      confirmDelete,
      updateLead,
      closeEditModal,
      closeDeleteModal,
      closeViewModal,
      closeToast,
      formatDate,
      toggleSelectAll,
      getShortId,
      getLeadImageUrl,
      getCompanyName,
      getAssignedUserName,
      showAssignModal,
      showBulkAssignModal,
      closeAssignModal,
      assignLead,
      assignSelectedLeads,
      isBulkAssign,
      getStatusClass,
      handlePageChange,
      viewLead,
      handleSearch,
      directT,
      loadingCustomFields,
    };
  },
});
</script>
